import React, { useState, useEffect } from 'react';
import { I18n } from 'react-redux-i18n';
import styled from 'styled-components';

import Popup from 'components/Popup';
import Checkbox from 'components/Checkbox';
import Icon from 'components/Icon';

const ColumnVisibilityContainer = styled.div`
  .buttonColumns {
    margin-right: 10px;
    border: 0.8px black solid;
    background-color: white;
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    color: ${(props) => props.theme.colors.black};
    border-radius: 24px;
    height: 30px;
    white-space: nowrap;
    padding-left: 15px;
    padding-right: 15px;
    cursor: pointer;

    p {
      margin-left: 10px;
      font-size: 12px;
    }

    span {
      color: ${(props) => props.theme.colors['terra-cotta']};
    }

    &:hover {
      opacity: 0.6;
    }
  }

  .topContentModal {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 20px;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
  }

  .modalSubtitle {
    text-transform: uppercase;
    color: #2b3d54;
    font-family: ${(props) => props.theme.fonts.oswald};

    span {
      color: ${(props) => props.theme.colors['terra-cotta']};
      margin-left: 5px;
    }
  }

  .topRightContentModal {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-left: auto;

    .displayAll {
      color: #153749;
      font-size: 12px;
      font-family: ${(props) => props.theme.fonts.sfcompact};
      margin-left: 10px;
    }
  }

  .listColumns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 50px;
    width: 100%;
  }

  .columnItem {
    width: calc(100% - 40px);
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 20px 20px;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
  }

  .itemLabel {
    margin-left: 10px;
    font-size: 12px;
    color: #153749;
  }

  .columnItemDark {
    background-color: rgba(245, 245, 245, 0.8);
  }
`;

type ColumnVisibilityModalProps = {
  columnsNames: string[];
  hiddenColumns: string[];
  onApply: (hiddenColumns: string[]) => void;
};

const ColumnVisibilityModal: React.FC<ColumnVisibilityModalProps> = ({
  columnsNames,
  hiddenColumns,
  onApply
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [tmpHiddenColumns, setTmpHiddenColumns] =
    useState<string[]>(hiddenColumns);

  useEffect(() => {
    setTmpHiddenColumns(hiddenColumns);
  }, [hiddenColumns]);

  const handleColumnVisibility = (value: string) => {
    const isHidden = tmpHiddenColumns.includes(value);
    if (isHidden) {
      setTmpHiddenColumns(tmpHiddenColumns.filter((c) => c !== value));
    } else {
      setTmpHiddenColumns([...tmpHiddenColumns, value]);
    }
  };

  const handleOpen = () => {
    setTmpHiddenColumns(hiddenColumns);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
  };

  const handleOk = () => {
    onApply(tmpHiddenColumns);
    setModalOpen(false);
  };

  return (
    <ColumnVisibilityContainer>
      <div className="buttonColumns" onClick={handleOpen}>
        <Icon icon="eyeClosed" alt="" />
        <p>
          {I18n.t('restaurants&caves.kpi.labelButtonVisibilityColumns')}{' '}
          <span>
            {hiddenColumns.length
              ? `(${columnsNames.length - hiddenColumns.length})`
              : ''}
          </span>
        </p>
      </div>
      <Popup
        open={modalOpen}
        title={I18n.t('restaurants&caves.kpi.modalTitle')}
        icon="eyeClosed"
        onClose={handleClose}
        okLabel={`${I18n.t('restaurants&caves.kpi.modalAccept')} (${
          columnsNames.length - tmpHiddenColumns.length
        })`}
        onOk={handleOk}
      >
        <div className="topContentModal">
          <p className="modalSubtitle">
            {I18n.t('restaurants&caves.kpi.modalSubtitle')}
            <span>({columnsNames.length})</span>
          </p>
          <div className="topRightContentModal">
            <Checkbox
              checked={tmpHiddenColumns.length === 0}
              onChange={() =>
                setTmpHiddenColumns(
                  tmpHiddenColumns.length > 0 ? [] : [...columnsNames]
                )
              }
              disabled={false}
              styleDisabled={undefined}
              style={undefined}
            />
            <p className="displayAll">
              {I18n.t('restaurants&caves.kpi.displayAll')}
            </p>
          </div>
        </div>
        <div className="listColumns">
          {columnsNames.map((item, index) => (
            <div
              key={index}
              className={
                index % 4 === 0 || (index - 1) % 4 === 0
                  ? 'columnItem columnItemDark'
                  : 'columnItem'
              }
            >
              <Checkbox
                checked={!tmpHiddenColumns.includes(item)}
                onChange={() => handleColumnVisibility(item)}
                disabled={false}
                styleDisabled={undefined}
                style={undefined}
              />
              <p className="itemLabel">{item}</p>
            </div>
          ))}
        </div>
      </Popup>
    </ColumnVisibilityContainer>
  );
};

export default ColumnVisibilityModal;
